import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";

import { nuvraRuntime } from "@/runtime/coreRuntime";
import { registerCoreModules } from "@/runtime/modules";
import { startAutoHealing } from "@/ai/selfHealing/autoHealRunner";
import { loadLearningFromDB } from "@/ai/selfHealing/learningEngine";
import { attachGlobalErrorHandlers } from "@/system/globalErrorHandler";
import { initProjectSync } from "@/sync/initProjectSync";
import { initArtifactHydration } from "@/sync/initArtifactHydration";
import { initSupabaseAuth } from "@/auth/initSupabaseAuth";
import { initAppearance } from "@/system/appearance";
import { registerServiceWorker } from "@/system/registerServiceWorker";
import { installNativeApiOrigin } from "@/shell/apiOrigin";
import { initNativeShell } from "@/shell/nativeShell";
import { markAppReady } from "@/system/bootSplash";
import { clearChunkRecoveryMarker, installChunkLoadRecovery } from "@/system/chunkError";

/* =========================================================
API ORIGIN (before anything can fetch, including React)
Inside the native shell a relative /api path resolves to
Capacitor's own asset server, which answers with index.html
and HTTP 200. Every caller then fails parsing HTML as JSON.
This points those requests at the real server. A no-op on
the web, where a relative path is already correct.
========================================================= */

installNativeApiOrigin();

/* =========================================================
CHUNK RECOVERY (before React/lazy routes)
A deploy can leave an open tab holding an older app shell that
references hashed chunks removed by the new deployment. Install
Vite/browser recovery before mounting the route tree so the whole
application can self-heal instead of showing a dead route.
========================================================= */

installChunkLoadRecovery();
clearChunkRecoveryMarker();

/* =========================================================
APPEARANCE (before first paint)
Apply the persisted theme and density so a reload never
flashes the wrong palette and choices work on every page.
========================================================= */

initAppearance();

/* =========================================================
AUTH WIRING (before first render so authEngine is ready)
Wire Supabase session events into authEngine so workspace
preview panels never flash the login form for authenticated
builders, and protected-page guards have correct auth state.
========================================================= */

initSupabaseAuth();

/* =========================================================
MOUNT UI FIRST (CRITICAL)
========================================================= */

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

createRoot(rootElement).render(<App />);

/* =========================================================
RETIRE THE BOOT SCREEN (after the first real paint)
Two frames, not one: the first schedules the paint, the
second runs once it has happened, so the boot screen is
never removed while the app is still blank.
========================================================= */

requestAnimationFrame(() => requestAnimationFrame(() => markAppReady()));

/* =========================================================
GLOBAL ERROR CAPTURE (after mount - non-React async errors)
========================================================= */

attachGlobalErrorHandlers();

// Register the push service worker (safe, idempotent, no offline caching).
registerServiceWorker();

// Native shell setup: status bar, hardware back button, keyboard, splash dismissal, and the
// re-send of any store purchase that never reached the server. A no-op on the web, and after the
// first paint so nothing here can delay the app appearing.
void initNativeShell();

/* =========================================================
BOOT SYSTEMS IN BACKGROUND (SAFE)
========================================================= */

async function bootstrap() {
  try {
    registerCoreModules();

    await loadLearningFromDB();

    await nuvraRuntime.start();

    // Wire project-level cloud sync (real Supabase, owner-scoped). No-op without
    // a Supabase session; only syncs once a project is active. Auto-syncs so work
    // resumes across devices.
    initProjectSync({ autoSync: true });

    // Auto-load a project's strategic memory (blueprint, discovery, recommendations)
    // whenever it opens, so Nuvra's reasoning follows the user across devices.
    initArtifactHydration();

    if (import.meta.env.DEV) {
      startAutoHealing(15000);
    }
  } catch (err) {
    console.error("🔥 Bootstrap failed:", err);
  }
}

bootstrap();